/**
 * Custom authentication hook that wraps Auth0 and integrates with our app store.
 */

import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";

import { getAuth0Config } from "./config";
import { tokenService } from "./tokenService";

export interface TAuthUser {
    id?: string;
    email?: string;
    displayName?: string;
    companyId?: string;
    companyName?: string;
    isAdmin: boolean;
}

export function useAuth() {
    const {
        isAuthenticated,
        isLoading,
        user: auth0User,
        loginWithRedirect,
        logout: auth0Logout,
        getAccessTokenSilently,
        error,
    } = useAuth0();

    const config = getAuth0Config();

    /**
     * Get an access token for API calls.
     * This will refresh the token if needed. When the cached session cannot be
     * renewed, the failure is reported to ProtectedRoute (via tokenService) so
     * the app re-authenticates instead of rendering without a user.
     */
    const getAccessToken = useCallback(async (): Promise<string | null> => {
        if (!isAuthenticated) {
            return null;
        }
        try {
            return await getAccessTokenSilently({
                authorizationParams: {
                    audience: config.audience,
                },
            });
        } catch (err) {
            console.error("Failed to get access token:", err);
            tokenService.notifyAuthError();
            return null;
        }
    }, [isAuthenticated, getAccessTokenSilently, config.audience]);

    /**
     * Login the user, redirecting to Auth0.
     */
    const login = useCallback(
        (returnTo?: string) => {
            loginWithRedirect({
                appState: { returnTo: returnTo || window.location.pathname },
            });
        },
        [loginWithRedirect],
    );

    /**
     * Logout the user.
     */
    const logout = useCallback(() => {
        auth0Logout({
            logoutParams: {
                returnTo: window.location.origin,
            },
        });
    }, [auth0Logout]);

    /**
     * Convert Auth0 user to our TAuthUser type.
     */
    const user: TAuthUser | null = auth0User
        ? {
              id: auth0User.sub,
              email: auth0User.email,
              displayName: auth0User.name || auth0User.nickname || auth0User.email,
              isAdmin: false, // This will be set from the backend
          }
        : null;

    return {
        isAuthenticated,
        isLoading,
        user,
        error,
        login,
        logout,
        getAccessToken,
    };
}
